import { useQueries, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import type { ConsumptionEvent, GroupPhoto, SessionBootstrap } from '@lbc/shared';

import { api, ApiClientError } from '../../services/api/client';
import { identityStore, installationStore } from '../../services/persistence/identity-store';
import {
  createWrappedMonthlyComparison,
  createWrappedMonthlyStats,
  createWrappedStats,
  isAnnualWrappedAvailable,
  wrappedMonthlyTargetMonthKey,
  type WrappedDrinkEvent,
} from './wrapped-model';
import { GroupWrappedSection } from './wrapped-group-section';
import { MonthlyWrappedSection } from './wrapped-monthly-section';
import { PersonalWrappedSection } from './wrapped-personal-section';
import { WrappedHeader } from './wrapped-primitives';

interface WrappedPageProps {
  onBack: () => void;
}

export function WrappedPage({ onBack }: WrappedPageProps) {
  const installation = installationStore.read();
  const legacyIdentity = identityStore.read();
  const memberships = installation?.memberships ?? [];
  const initialGroupId = installation?.lastSelectedGroupId ?? legacyIdentity?.groupId ?? null;
  const groupIds =
    memberships.length > 0 ? memberships.map((membership) => membership.groupId) : [null];
  const [selectedGroupId, setSelectedGroupId] = useState(initialGroupId);
  const selectedIndex = Math.max(
    0,
    groupIds.findIndex((groupId) => groupId === selectedGroupId),
  );
  const groupQueries = useQueries({
    queries: groupIds.map((groupId) => ({
      queryKey: ['wrapped-bootstrap', groupId ?? 'legacy'],
      queryFn: () => (groupId === null ? api.bootstrap() : api.wrappedGroupBootstrap(groupId)),
      staleTime: 30_000,
    })),
  });
  const photoQueries = useQueries({
    queries: groupIds.map((groupId) => ({
      queryKey: ['wrapped-photo-highlights', groupId ?? 'legacy'],
      queryFn: () =>
        groupId === null ? Promise.resolve([] as GroupPhoto[]) : api.groupPhotoHighlights(groupId),
      staleTime: 30_000,
    })),
  });
  const personalQuery = useQuery({
    queryKey: ['wrapped-personal-consumptions', installation?.installationId],
    queryFn: () => api.consumptions(),
    enabled: installation !== null,
    staleTime: 30_000,
  });
  const selectedQuery = groupQueries[selectedIndex];
  const selectedBootstrap = selectedQuery?.data;
  const selectedHighlights = photoQueries[selectedIndex]?.data ?? [];
  const allQueriesReady =
    groupQueries.every((query) => query.data !== undefined) &&
    photoQueries.every((query) => query.data !== undefined) &&
    (installation === null || personalQuery.data !== undefined);
  const firstError =
    personalQuery.error ??
    groupQueries.find((query) => query.error !== null)?.error ??
    photoQueries.find((query) => query.error !== null)?.error;
  const personalContext = groupQueries[selectedIndex]?.data;
  const bootstrapsByGroup = new Map<string, SessionBootstrap>(
    groupQueries.flatMap((query) =>
      query.data === undefined ? [] : [[query.data.group.id, query.data] as [string, SessionBootstrap]],
    ),
  );
  const personalEvents = (() => {
    if (personalContext === undefined) return [];
    if (installation !== null && personalQuery.data !== undefined) {
      return personalQuery.data
        .filter((event) => event.deletedAt === null && event.projections.length > 0)
        .flatMap((event) => personalConsumptionAsDrinkEvents(event, bootstrapsByGroup, personalContext));
    }
    const uniqueEvents = new Map<string, WrappedDrinkEvent>();
    for (const query of groupQueries) {
      const bootstrap = query.data;
      if (bootstrap === undefined) continue;
      for (const event of bootstrap.drinkEvents) {
        if (event.deletedAt === null && event.participantId === bootstrap.participant.id) {
          const contextual = bootstrapsByGroup.get(bootstrap.group.id) ?? personalContext;
          uniqueEvents.set(event.id, {
            ...event,
            groupId: contextual.group.id,
            participantId: personalContext.participant.id,
            wrappedTimezone: contextual.group.timezone,
            wrappedDayBoundaryHour: contextual.group.dayBoundaryHour,
          });
        }
      }
    }
    return [...uniqueEvents.values()];
  })();
  const personalStats =
    personalContext === undefined
      ? null
      : createWrappedStats(
          {
            ...personalContext,
            participants: [personalContext.participant],
          },
          personalEvents,
        );
  const wrappedReferenceDate = new Date();
  const personalMonthlyTargetMonthKey =
    personalContext === undefined
      ? null
      : wrappedMonthlyTargetMonthKey(personalContext.group.timezone, wrappedReferenceDate);
  const handleBack = () => {
    if (personalMonthlyTargetMonthKey !== null) {
      const periodKey = `monthly-${personalMonthlyTargetMonthKey}`;
      const pendingKey = `lbc.wrapped.pending-reminder.${periodKey}`;
      if (window.localStorage.getItem(pendingKey) === '1') {
        window.localStorage.setItem(`lbc.wrapped.viewed.${periodKey}`, 'viewed');
        window.localStorage.removeItem(pendingKey);
      }
    }
    onBack();
  };
  const monthlyComparison =
    personalContext === undefined || personalMonthlyTargetMonthKey === null
      ? null
      : createWrappedMonthlyComparison(
          personalContext,
          personalEvents,
          wrappedReferenceDate,
          personalMonthlyTargetMonthKey,
        );
  const groupStats = selectedBootstrap === undefined ? null : createWrappedStats(selectedBootstrap);
  const monthlyGroupStats =
    selectedBootstrap === undefined
      ? null
      : createWrappedMonthlyStats(
          selectedBootstrap,
          wrappedMonthlyTargetMonthKey(selectedBootstrap.group.timezone, wrappedReferenceDate),
          selectedBootstrap.drinkEvents,
          wrappedReferenceDate,
        );
  const groupName = selectedBootstrap?.group.name ?? 'Deine Gruppe';
  const hasGroupPicker = memberships.length > 1;

  if (firstError !== undefined) {
    return (
      <main className="wrapped-page">
        <WrappedHeader onBack={handleBack} />
        <section className="wrapped-loading">
          <p className="form-error" role="alert">
            {readableWrappedError(firstError)}
          </p>
          <button
            className="button button--secondary"
            type="button"
            onClick={() => void Promise.all(groupQueries.map((query) => query.refetch()))}
          >
            Erneut versuchen
          </button>
        </section>
      </main>
    );
  }

  if (
    !allQueriesReady ||
    personalStats === null ||
    groupStats === null ||
    monthlyGroupStats === null ||
    selectedBootstrap === undefined
  ) {
    return (
      <main className="wrapped-page">
        <WrappedHeader onBack={handleBack} />
        <section className="wrapped-loading" aria-live="polite">
          <p className="step-label">LBC Wrapped</p>
          <h1>Deine Auswertung wird geladen …</h1>
          <p>Die bisher erfassten Einträge aus deinen Gruppen werden gerade zusammengetragen.</p>
        </section>
      </main>
    );
  }

  if (
    personalContext !== undefined &&
    monthlyComparison !== null &&
    monthlyGroupStats !== null &&
    !isAnnualWrappedAvailable(personalContext.group.timezone)
  ) {
    return (
      <MonthlyWrappedSection
        comparison={monthlyComparison}
        groupStats={monthlyGroupStats}
        groupName={groupName}
        memberships={memberships}
        selectedGroupId={selectedGroupId}
        hasGroupPicker={hasGroupPicker}
        onSelectGroup={setSelectedGroupId}
        onBack={handleBack}
        highlights={selectedHighlights}
      />
    );
  }

  return (
    <main className="wrapped-page">
      <WrappedHeader onBack={handleBack} />

      <PersonalWrappedSection stats={personalStats} />

      <section className="wrapped-group-section" aria-labelledby="wrapped-group-section-title">
        {hasGroupPicker ? (
          <label className="wrapped-group-select">
            <span>Gruppe auswählen</span>
            <select
              value={selectedGroupId ?? ''}
              onChange={(event) => setSelectedGroupId(event.target.value)}
            >
              {memberships.map((membership) => (
                <option value={membership.groupId} key={membership.id}>
                  {membership.groupName ?? 'Ohne Gruppennamen'} · {membership.displayName}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <div className="wrapped-section-heading">
          <div>
            <p className="step-label">Teil zwei</p>
            <h2 id="wrapped-group-section-title">Gruppenauswertung</h2>
            <p>Jetzt schauen wir auf {groupName} als gemeinsame Gruppe.</p>
          </div>
        </div>
        <GroupWrappedSection
          stats={groupStats}
          groupName={groupName}
          highlights={selectedHighlights}
        />
      </section>
    </main>
  );
}

function readableWrappedError(error: unknown): string {
  if (error instanceof ApiClientError) return error.message;
  return 'Die Auswertung konnte gerade nicht geladen werden.';
}

function personalConsumptionAsDrinkEvents(
  event: ConsumptionEvent,
  bootstraps: Map<string, SessionBootstrap>,
  fallback: Pick<SessionBootstrap, 'group' | 'participant'>,
): WrappedDrinkEvent[] {
  const projection = [...event.projections].sort((left, right) =>
    `${left.groupId}:${left.participantId}:${left.drinkEventId}`.localeCompare(
      `${right.groupId}:${right.participantId}:${right.drinkEventId}`,
    ),
  )[0];
  if (projection === undefined) return [];
  const context = bootstraps.get(projection.groupId) ?? fallback;
  return [{
    id: event.id,
    groupId: projection.groupId,
    participantId: projection.participantId,
    occurredAt: event.occurredAt,
    volumeMl: event.volumeMl,
    abv: event.abv,
    createdAt: event.createdAt,
    updatedAt: event.updatedAt,
    deletedAt: event.deletedAt,
    wrappedTimezone: context.group.timezone,
    wrappedDayBoundaryHour: context.group.dayBoundaryHour,
  }];
}
